import type { QueryClient } from '@tanstack/react-query';
import type { AugmentedAccount, AugmentedUser, Workspace } from '@/services/auth/types';
import { userSettingsKeys, type SideNavPreferences, type UserSettings } from './entity';
import { authKeys } from '@/services/auth/query-keys';

export type AuthenticatedQueryData = {
  account: AugmentedAccount | null;
  user: AugmentedUser | null;
  workspaces: Workspace[] | null;
};

/** Patch the authenticated user's side-nav preferences into the `['authenticated']` cache. */
export function setAuthenticatedSideNavPreferences(qc: QueryClient, sideNavPreferences: SideNavPreferences): void {
  const auth = qc.getQueryData<AuthenticatedQueryData>(authKeys.authenticated());

  if (auth?.user == null) {
    return;
  }

  qc.setQueryData<AuthenticatedQueryData>(authKeys.authenticated(), {
    ...auth,
    user: {
      ...auth.user,
      settings: {
        ...auth.user.settings,
        side_nav_preferences: sideNavPreferences,
      },
    },
  });
}

/** Write user settings into their detail slot and mirror side-nav prefs onto the authenticated user. */
export function setUserSettingsCache(
  qc: QueryClient,
  settings: UserSettings,
  fallbackSideNavPreferences?: SideNavPreferences,
): void {
  const sideNavPreferences = settings.side_nav_preferences ?? fallbackSideNavPreferences;

  qc.setQueryData(userSettingsKeys.detail(settings.accountId, settings.userId), settings);

  if (sideNavPreferences != null) {
    setAuthenticatedSideNavPreferences(qc, sideNavPreferences);
  }
}

/** Roll the user-settings detail slot and `['authenticated']` cache back to a pre-mutation snapshot. */
export function restoreUserSettingsCache(
  qc: QueryClient,
  accountId: number | null | undefined,
  userId: number | null | undefined,
  previousUserSettings: UserSettings | undefined,
  previousAuthenticatedQueryData: AuthenticatedQueryData | undefined,
): void {
  if (accountId != null && userId != null) {
    if (previousUserSettings == null) {
      qc.removeQueries({ exact: true, queryKey: userSettingsKeys.detail(accountId, userId) });
    } else {
      qc.setQueryData(userSettingsKeys.detail(accountId, userId), previousUserSettings);
    }
  }

  if (previousAuthenticatedQueryData == null) {
    qc.removeQueries({ exact: true, queryKey: authKeys.authenticated() });
  } else {
    qc.setQueryData<AuthenticatedQueryData>(authKeys.authenticated(), previousAuthenticatedQueryData);
  }
}
